/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {CSSProperties, useState} from 'react';
import {makeStyles, RadioGroup} from '@material-ui/core';
import isObject from 'lodash.isobject';
import {dlsColor, dlsTypography} from '../../theme/theme';
import RadioButton from '../radio/RadioButton';
import {FormItem} from './FormItem';

// defiens the prop definitions for radio options ...
export type Option = {
  label: string;
  value: any;
  disabled: boolean;
};

// defines the form radio group prop definitions ...
export interface FormRadioGroupProps {
  onChange: (value: any, groupLabel: string) => void;
  groupLabel: string;
  name?: string;
  formItemStyle?: CSSProperties;
  options: Option[];
  defaultValue?: any;
  displayAsRow?: boolean;
  required?: boolean;
};

// defines the form radio group styles ...
export const useStyles = makeStyles(theme => ({
  radioGroupWrapper : {
    margin  : 0,
    padding : 0
  },
  groupLabel : {
    color         : dlsColor('grey2'),
    marginBottom  : '8px',
    textTransform : 'capitalize',
    ...dlsTypography('body3')
  },
  requiredAsterisk : {
    color  : dlsColor('aaBlue'),
    margin : '0 2px'
  },
  root : {
    margin        : '15px 0 0 15px',
    flexDirection : ({displayAsRow}: {displayAsRow: boolean}) =>
      displayAsRow? 'row !important' : 'column'
  }
}));

/**
 * @FormRadioGroup
 *
 * The FormRadioGroup component provides a generalized DLS component which is typically used
 * within the various Entity Editors for displaying and setting radio options ...
 *
 * ```typescript
 *
 *   import {FormRadioGroup} from '@ion/dls/src/components/buttons/';
 *
 *   <FormRadioGroup options={options}
 *     groupLabel={translate('common:editors.labels.inheritable')}
 *     displayAsRow
 *     onChange={handleRadioGroupChange} />
 *
 * ```
 *
 * @param {Option[]} options The radio options
 * @param {string}   groupLabel The label for radio group
 * @param {boolean}  displayAsRow When true, the radio options are displayed as row
 *                   or defaults to column
 * @param {Function} onChange Callback function invoked on change of radio option giving the
 *                   value of the selected radio option
 */
export const FormRadioGroup = ({
  formItemStyle,
  options,
  groupLabel,
  defaultValue,
  onChange,
  name,
  displayAsRow = false,
  required = false,
  ...props
}: FormRadioGroupProps) => {
  const classes = useStyles({displayAsRow});
  const [selectedValue, setSelectedValue] = useState(defaultValue);

  const handleChange = function({target}) {
    const val = target?.value;
    const selectedVal = options?.filter(({value}) => value?.toString() === val)[0];
    setSelectedValue(selectedVal?.value);
    onChange?.(val, name || groupLabel);
  };

  return (
    <FormItem fullWidth>
      <div className={classes.radioGroupWrapper}
        {...(isObject(formItemStyle) && {
          style : formItemStyle
        })}>
        <span className={classes.groupLabel}>{groupLabel}</span>
        {required && <span className={classes.requiredAsterisk}>*</span>}
        <RadioGroup name={groupLabel} classes={{
          root : classes.root
        }}>
          {options?.map((option, index) => (
            <RadioButton key={index}
              label={option.label}
              onChange={handleChange}
              checked={selectedValue === option.value}
              disabled={option.disabled}
              value={option.value}
              size='small'
              {...props} />
          ))}
        </RadioGroup>
      </div>
    </FormItem>
  );
};

export default FormRadioGroup;
